import React, { useEffect, useCallback } from 'react';

import { Grid, Section } from '@gogame/components';
import { FormField, getIn } from '@gogame/form';

import { loadMonthlyBudget } from 'scenes/IO/sources/budget';
import { loadCurrencies } from 'scenes/IO/sources/fields';

import {
  AMOUNT,
  CURRENCY_CODE,
  MONTHLY,
  DEPENDENCIES,
  PAYMENT_TERMS,
  EXPENSE_TYPE,
  RATE_OF_NET,
} from '../constants';
import {
  monthlyBudgetValidationSchema,
  monthlyBudgetParamsSchema,
  currencyValidationSchema,
} from '../schemas';
import MonthlyBudget from './MonthlyBudget';
import TotalBudgetFormatter from './TotalBudgetFormatter';

const REF_SHARE_ID = 2;

const Budget = ({ area, form, name, disabled, value = {} }) => {
  const expenseType = getIn(form.values, `${PAYMENT_TERMS}.${EXPENSE_TYPE}`);
  const isRefShare = expenseType === REF_SHARE_ID;
  const { data, loading } = area;
  useEffect(() => {
    if (
      expenseType !==
      getIn(form.initialValues, `${PAYMENT_TERMS}.${EXPENSE_TYPE}`)
    ) {
      form.setFieldValue(`${name}`, {
        ...value,
        [AMOUNT]: 0,
        [MONTHLY]: [],
      });
    }
  }, [expenseType]);

  useEffect(() => {
    form.setFieldValue(`${name}.${isRefShare ? AMOUNT : RATE_OF_NET}`, 0);
  }, [isRefShare]);

  const validateRegularBudget = useCallback(
    (_value, { sendForApproval }) => {
      if ((!data && !loading) || !sendForApproval) {
        return;
      }
      return loading
        ? '  ' /* keep stay invalid during loading */
        : data?.validation?.isValid
          ? undefined
          : data?.validation?.errors[0];
    },
    [data, loading]
  );

  const validateRefShareBudget = useCallback((value, { sendForApproval }) => {
    if (!sendForApproval) {
      return;
    }

    if (value === undefined || +value < 1) {
      return 'Rate of net Rev. should not be equal to 0';
    }
  }, []);

  return (
    <Section title={'Budget'}>
      <Grid container spacing={3}>
        <Grid item container xs={12} spacing={3}>
          <Grid item xs={2}>
            {isRefShare ? (
              <FormField.TextField
                type={'percent'}
                placeholder={'0'}
                padding={'0'}
                label={'Rate of Net Rev.'}
                name={`${name}.${RATE_OF_NET}`}
                disabled={disabled}
                loading={area.loading}
                currency={value[CURRENCY_CODE]}
                validate={validateRefShareBudget}
                InputLabelProps={{
                  shrink: true,
                }}
                helper={
                  area.data ? <TotalBudgetFormatter {...area.data} /> : null
                }
              />
            ) : (
              <FormField.TextField
                type={'currency'}
                placeholder={'0'}
                padding={'0'}
                required={true}
                label={'Total budget'}
                name={`${name}.${AMOUNT}`}
                debounceTimeout={300}
                validate={validateRegularBudget}
                disabled={disabled}
                loading={area.loading}
                currency={value[CURRENCY_CODE]}
                InputLabelProps={{
                  shrink: true,
                }}
                helper={
                  area.data ? <TotalBudgetFormatter {...area.data} /> : null
                }
              />
            )}
          </Grid>
          <Grid item xs={2}>
            <FormField.Select
              label={'Currency'}
              name={`${name}.${CURRENCY_CODE}`}
              depends={DEPENDENCIES[`${name}.${CURRENCY_CODE}`]}
              active={currencyValidationSchema.isValidSync(form.values)}
              disabled={disabled}
              source={loadCurrencies}
              padding={'0'}
            />
          </Grid>
        </Grid>

        {isRefShare ? null : (
          <Grid item xs={12}>
            <MonthlyBudget
              name={`${name}.${MONTHLY}`}
              source={loadMonthlyBudget}
              averageMonthlyAmount={area.data && area.data.average}
              params={(values) => monthlyBudgetParamsSchema.cast(values)}
              depends={DEPENDENCIES[`${name}.${MONTHLY}`]}
              disabled={disabled}
              currency={value[CURRENCY_CODE]}
              budgetAmount={value[AMOUNT]}
              active={monthlyBudgetValidationSchema.isValidSync(form.values)}
            />
          </Grid>
        )}
      </Grid>
    </Section>
  );
};

export default Budget;
